import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, TransferState } from '@angular/core';
import { BehaviorSubject, first, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { BlogRoot, Story } from './blog.types';
import { BlockTypes, TextNode } from '@storyblok/richtext';
import { LanguageService } from '../language/language.service';
import { ARTICLES_STATE_KEY, STORYBLOK_API } from './blog.constants';

@Injectable({
  providedIn: 'root',
})
export class BlogService {
  private readonly httpClient = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly platformID = inject(PLATFORM_ID);
  private readonly langService = inject(LanguageService);
  private readonly data = new BehaviorSubject<Story[]>([]);

  public data$ = this.data.asObservable();

  public getAllArticles() {
    if (isPlatformServer(this.platformID)) {
      this.fetchArticles().subscribe();
    } else if (isPlatformBrowser(this.platformID)) {
      const stories = this.transferState.get<Story[]>(ARTICLES_STATE_KEY, []);
      if (stories.length) {
        this.data.next(stories);
      } else {
        this.fetchArticles().subscribe();
      }
    }
  }

  private fetchArticles(): Observable<void> {
    return this.httpClient
      .get<BlogRoot>(
        STORYBLOK_API.replace(
          '{{version}}',
          environment.storyblok_api_version,
        ).replace('{{api_key}}', environment.storyblok_api_key),
      )
      .pipe(
        map((blog) => {
          const stories = blog.stories;
          stories.map((story) =>
            story.content.components.map((comp) => {
              if (comp.component === 'content') {
                this.processRichTextNodeForHref(comp.article_body);
                this.processNodeForImage(comp.article_body);
              }
            }),
          );
          this.transferState.set(ARTICLES_STATE_KEY, stories);
          this.data.next(stories);
        }),
        first(),
      );
  }

  private processNodeForImage(node?: TextNode) {
    node?.content.map((block) => {
      if (block.content !== undefined) {
        block.content.map((childBlock) => {
          if (childBlock.type == 'image') {
            block.type = BlockTypes.IMAGE;
            block.content = [];
            block.attrs = childBlock.attrs;
          }
        });
      }
    });
  }

  private processRichTextNodeForHref(node?: TextNode): void {
    if (node?.marks) {
      node.marks.forEach((mark) => {
        if (mark.type === 'link' && mark.attrs?.href.startsWith('/blog')) {
          mark.attrs.target = '_self';
          mark.attrs.href =
            '/' + this.langService.activeLanguage() + mark.attrs?.href;
        }
      });
    }

    if (node?.content) {
      node.content.forEach((childNode) =>
        this.processRichTextNodeForHref(childNode as TextNode),
      );
    }
  }
}
